import { Viewer } from '../../../viewer/viewer';
import { HelperLine } from '../../../utils/HelperLine';
import { PointoramaPlane } from '../../../utils/PointoramaPlane';
import * as THREE from 'three';
import { LabelText } from '../../../utils/LabelText';
import { DefaultColors } from '../../../defines';
import { DebugPointVisualizer } from '../../../utils/DebugPointVisualizer';
import { getPointsAlongRay, PointWithSegment, IPointCloud } from '../../../utils/PointCloudMathUtils';

// Ray radius scaling constants for distance-based picking
const RAY_PICK_MIN_RADIUS = 0.01;
const RAY_PICK_DISTANCE_SCALE_FACTOR = 0.1;
const RAY_PICK_SPACING_MULTIPLIER = 4; // Max radius = point's LOD spacing * this multiplier
const RAY_PICK_MAX_POINTS = 500;
const MAX_RADIUS_SMOOTHING_FRAMES = 15; // Number of frames to smooth maxRadius over

// Performance profiling utility
class PerformanceProfiler {
  private timings: Map<string, number[]> = new Map();
  private startTimes: Map<string, number> = new Map();

  start(label: string): void {
    this.startTimes.set(label, performance.now());
  }

  end(label: string): number {
    const startTime = this.startTimes.get(label);
    if (startTime === undefined) {
      return 0;
    }

    const duration = performance.now() - startTime;
    this.startTimes.delete(label);

    if (!this.timings.has(label)) {
      this.timings.set(label, []);
    }
    const timingArray = this.timings.get(label);
    if (timingArray) timingArray.push(duration);

    return duration;
  }

  getStats(label: string): { count: number; total: number; avg: number; min: number; max: number } | null {
    const times = this.timings.get(label);
    if (!times || times.length === 0) {
      return null;
    }

    const total = times.reduce((sum, t) => sum + t, 0);
    const avg = total / times.length;
    const min = Math.min(...times);
    const max = Math.max(...times);

    return { count: times.length, total, avg, min, max };
  }

  clear(): void {
    this.timings.clear();
    this.startTimes.clear();
  }

  printSummary(): void {
    const labels = Array.from(this.timings.keys()).sort();
    const statsObj: Record<string, { calls: number; total: number; avg: number; min: number; max: number }> = {};

    for (const label of labels) {
      const stats = this.getStats(label);
      if (stats) {
        statsObj[label] = {
          calls: stats.count,
          total: stats.total,
          avg: stats.avg,
          min: stats.min,
          max: stats.max,
        };
      }
    }

    // Export to window for debug widget
    (window as Window & { DEBUG_PLANE_PROFILING_STATS?: typeof statsObj }).DEBUG_PLANE_PROFILING_STATS = statsObj;
  }
}

type MousePoints = {
  center: THREE.Vector3;
  grid: THREE.Vector3[];
  clusterPoints?: WeightedPoint[]; // Store cluster points with each marker
  isSnapped?: boolean; // Track if this marker represents a snapped plane
  snappedNormal?: THREE.Vector3; // Store the snapped normal if applicable
};

type WeightedPoint = {
  point: THREE.Vector3;
  weight: number;
};

type SegmentReference = {
  pointcloudId: string;
  segmentId: number;
};

type PointCloudIntersection = {
  position: THREE.Vector3;
  pointCloudId: string;
  segment: number[];
  distanceToRay: number;
};

type PointCloudNode = {
  name?: string;
  sceneNode?: {
    geometry?: THREE.BufferGeometry;
    matrixWorld?: THREE.Matrix4;
  };
  geometryNode?: {
    geometry?: THREE.BufferGeometry;
  };
  geometry?: THREE.BufferGeometry;
};

type PointCloudWithNodes = {
  id: string;
  identifier?: string;
  visibleNodes?: PointCloudNode[];
};

type PlaneData = {
  normal: THREE.Vector3;
  centroid: THREE.Vector3;
  quality: number;
  meanDistance: number;
  maxDistance: number;
  pointCount: number;
};

export class PlaneCreator extends THREE.Object3D {
  private static readonly CENTER_WEIGHT = 10000.0;
  private static readonly GRID_WEIGHT_BASE = 100.0;
  private static readonly GRID_WEIGHT_FALLOFF_RATE = 20.0;
  private static readonly CLUSTER_WEIGHT_BASE = 1000.0; // Higher base weight for cluster points
  private static readonly CLUSTER_WEIGHT_FALLOFF_RATE = 1.0; // Slower falloff for cluster points
  private static readonly MAX_WEIGHT = Math.max(PlaneCreator.CLUSTER_WEIGHT_BASE, PlaneCreator.GRID_WEIGHT_BASE); // Use cluster weight as max since it's highest
  private static readonly MAX_GRID_POINTS = 1500; // Maximum number of points to use for grid

  private static readonly CLUSTER_SAMPLE_SIZE = 1000;

  // Check debug flags from debug widget
  private static get DEBUG_RENDER(): boolean {
    return (window as Window & { DEBUG_PLANE_VISUALIZATION?: boolean }).DEBUG_PLANE_VISUALIZATION || false;
  }

  private static get ENABLE_PROFILING(): boolean {
    return (window as Window & { DEBUG_PLANE_PROFILING?: boolean }).DEBUG_PLANE_PROFILING || false;
  }

  // RANSAC parameters for plane fitting
  private static readonly RANSAC_MAX_ITERATIONS = 200;
  private static readonly RANSAC_OUTLIER_THRESHOLD = 0.001;
  private static readonly RANSAC_MIN_POINTS_RATIO = 0.3;

  // Plane distance tolerance parameters
  private static readonly BASE_PLANE_TOLERANCE = 0.05; // 5cm base tolerance
  private static readonly QUALITY_PLANE_TOLERANCE_FACTOR = 0.5; // Factor applied to quality-based tolerance adjustment
  private static readonly POINT_COUNT_PLANE_TOLERANCE_FACTOR = 20; // Factor for point count based tolerance adjustment

  // Normal smoothing parameters
  private static readonly NORMAL_SMOOTHING_FACTOR = 0.3; // Interpolation factor (0 = no smoothing, 1 = instant update)
  private static readonly NORMAL_CHANGE_THRESHOLD = 0.05; // Only smooth if angle change is less than ~2.8 degrees (cos(angle) > 0.95)

  viewer: Viewer;

  activePlane: PointoramaPlane;

  private profiler: PerformanceProfiler;
  private previousNormal: THREE.Vector3 | null = null; // Store previous normal for smoothing

  private static calculateGridWeight(gridPoint: THREE.Vector3, centerPoint: THREE.Vector3): number {
    const distance = gridPoint.distanceTo(centerPoint);
    return PlaneCreator.GRID_WEIGHT_BASE * Math.exp(-PlaneCreator.GRID_WEIGHT_FALLOFF_RATE * distance);
  }

  private smoothedMaxRadius = 0.1; // Exponentially smoothed maxRadius value

  private smoothMaxRadius(rawMaxRadius: number): number {
    // Asymmetric smoothing: jump down quickly, grow up slowly
    // This prevents outlier spikes from inflating the radius
    if (rawMaxRadius < this.smoothedMaxRadius) {
      // Going down - respond quickly (instant or near-instant)
      this.smoothedMaxRadius = rawMaxRadius;
    } else {
      // Going up - smooth slowly to reject outlier spikes
      const alpha = 1.0 / MAX_RADIUS_SMOOTHING_FRAMES;
      this.smoothedMaxRadius = alpha * rawMaxRadius + (1 - alpha) * this.smoothedMaxRadius;
    }
    return this.smoothedMaxRadius;
  }

  private smoothNormal(newNormal: THREE.Vector3): THREE.Vector3 {
    // If no previous normal, use the new one directly
    if (!this.previousNormal) {
      this.previousNormal = newNormal.clone();
      return newNormal;
    }

    // Calculate dot product to determine angle between normals
    const dotProduct = this.previousNormal.dot(newNormal);

    // If the change is large (dot product < threshold), update immediately
    if (dotProduct < 1 - PlaneCreator.NORMAL_CHANGE_THRESHOLD) {
      this.previousNormal.copy(newNormal);
      return newNormal;
    }

    // For small changes, interpolate (lerp) between previous and new normal
    const smoothedNormal = this.previousNormal.clone().lerp(newNormal, PlaneCreator.NORMAL_SMOOTHING_FACTOR).normalize();

    this.previousNormal.copy(smoothedNormal);
    return smoothedNormal;
  }

  private randomSample<T>(array: T[], sampleSize: number): T[] {
    if (array.length <= sampleSize) {
      return array;
    }

    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }

    return shuffled.slice(0, sampleSize);
  }

  private fitPlaneToPoints(points: THREE.Vector3[] | WeightedPoint[]): PlaneData | null {
    // Convert to weighted points if needed
    const weightedPoints: WeightedPoint[] = points.length > 0 && points[0] instanceof THREE.Vector3 ? (points as THREE.Vector3[]).map((p) => ({ point: p, weight: 1.0 })) : (points as WeightedPoint[]);

    if (weightedPoints.length < 3) {
      return {
        normal: new THREE.Vector3(0, 1, 0),
        centroid: new THREE.Vector3(),
        quality: 0,
        meanDistance: 0,
        maxDistance: 0,
        pointCount: weightedPoints.length,
      };
    }

    // Use PointoramaPlane's fitPlaneToPoints for accurate eigenvalue decomposition
    const planeFit = this.activePlane['fitPlaneToPoints'](weightedPoints);
    if (!planeFit) {
      return null;
    }

    // Calculate quality metrics
    let totalDistance = 0;
    let maxDistance = 0;
    let totalWeight = 0;

    weightedPoints.forEach((wp) => {
      const distToPlane = Math.abs(planeFit.normal.dot(wp.point.clone().sub(planeFit.centroid)));
      totalDistance += distToPlane * wp.weight;
      maxDistance = Math.max(maxDistance, distToPlane);
      totalWeight += wp.weight;
    });

    const meanDistance = totalDistance / totalWeight;

    // Calculate spatial extent for quality normalization
    const minPoint = weightedPoints[0].point.clone();
    const maxPoint = weightedPoints[0].point.clone();

    for (const wp of weightedPoints) {
      minPoint.min(wp.point);
      maxPoint.max(wp.point);
    }

    const spatialExtent = minPoint.distanceTo(maxPoint);
    const normalizedMeanDistance = meanDistance / spatialExtent;

    // Calculate quality using the same formula
    const k = 10; // Decay rate constant for normalized distance
    const quality = 100 / (1 + k * normalizedMeanDistance);

    return {
      normal: planeFit.normal,
      centroid: planeFit.centroid,
      quality,
      meanDistance,
      maxDistance,
      pointCount: weightedPoints.length,
    };
  }

  private getRandomPointsFromClusters(mouse: THREE.Vector2, mouseCenter?: THREE.Vector3, gridPlane?: PlaneData): WeightedPoint[] {
    const enableProfiling = PlaneCreator.ENABLE_PROFILING;
    if (enableProfiling) this.profiler.start('getRandomPointsFromClusters.total');
    const clusterPoints: WeightedPoint[] = [];

    // Use similar ray technique as grid points to find segments at the closest surface
    if (enableProfiling) this.profiler.start('getHoveredClusterSegmentsAtClosestSurface');
    const hoveredSegments = this.getHoveredClusterSegmentsAtClosestSurface(mouse);
    if (enableProfiling) this.profiler.end('getHoveredClusterSegmentsAtClosestSurface');

    if (hoveredSegments.length === 0) {
      if (enableProfiling) this.profiler.end('getRandomPointsFromClusters.total');
      return clusterPoints;
    }

    // Pre-calculate grid plane filtering parameters
    let planeDistanceTolerance = 0;
    let gridNormal: THREE.Vector3 | undefined;
    let gridCentroid: THREE.Vector3 | undefined;
    if (gridPlane) {
      const qualityNormalized = Math.max(0, Math.min(100, gridPlane.quality)) / 100;
      planeDistanceTolerance = PlaneCreator.BASE_PLANE_TOLERANCE + PlaneCreator.QUALITY_PLANE_TOLERANCE_FACTOR * (1 - qualityNormalized);
      planeDistanceTolerance *= Math.max(0.0, PlaneCreator.POINT_COUNT_PLANE_TOLERANCE_FACTOR / gridPlane.pointCount) + 1;
      gridNormal = gridPlane.normal;
      gridCentroid = gridPlane.centroid;

      if (PlaneCreator.DEBUG_RENDER) {
        console.log(`Grid plane: ${gridPlane.pointCount} points, quality: ${gridPlane.quality.toFixed(1)}, tolerance: ${planeDistanceTolerance.toFixed(3)}m`);
      }
    }

    // Extract points from each segment and evaluate plane fitting quality
    for (let i = 0; i < hoveredSegments.length; i++) {
      const segment = hoveredSegments[i];

      // Create cache key for this segment (based on segment ID only, not grid plane)
      const segmentCacheKey = `${segment.pointcloudId}_${segment.segmentId}`;

      // Check if we have cached sampled points for this segment
      if (enableProfiling) this.profiler.start('checkSegmentCache');
      let sampledPoints = this.segmentSamplesCache.get(segmentCacheKey);
      if (enableProfiling) this.profiler.end('checkSegmentCache');

      if (!sampledPoints) {
        // Cache miss - extract and sample points for the first time
        if (enableProfiling) this.profiler.start('extractPointsFromSegment');
        sampledPoints = this.extractPointsFromSegment(segment.pointcloudId, segment.segmentId);
        if (enableProfiling) this.profiler.end('extractPointsFromSegment');

        // Store sampled points in cache (already sampled by extractPointsFromSegment)
        this.segmentSamplesCache.set(segmentCacheKey, sampledPoints);
      }

      // Now filter the cached sampled points by grid plane if needed
      let filteredPoints = sampledPoints;
      if (gridNormal && gridCentroid) {
        if (enableProfiling) this.profiler.start('filterPointsByGridPlane');
        const filtered: THREE.Vector3[] = [];
        const gx = gridCentroid.x,
          gy = gridCentroid.y,
          gz = gridCentroid.z;
        const nx = gridNormal.x,
          ny = gridNormal.y,
          nz = gridNormal.z;
        for (let j = 0; j < sampledPoints.length; j++) {
          const point = sampledPoints[j];
          // Avoid creating temporary Vector3 objects - compute dot product directly
          const dx = point.x - gx;
          const dy = point.y - gy;
          const dz = point.z - gz;
          const distanceFromPlane = Math.abs(nx * dx + ny * dy + nz * dz);
          if (distanceFromPlane <= planeDistanceTolerance) {
            filtered.push(point);
          }
        }
        filteredPoints = filtered;
        if (enableProfiling) this.profiler.end('filterPointsByGridPlane');
      }

      // Fit a plane to filtered points to assess quality
      if (enableProfiling) this.profiler.start('fitPlaneToPoints.cluster');
      const planeFit = this.fitPlaneToPoints(filteredPoints);
      if (enableProfiling) this.profiler.end('fitPlaneToPoints.cluster');
      if (!planeFit) continue;

      // Add to cluster points with weights
      if (enableProfiling) this.profiler.start('weightClusterPoints');
      const cutOffPlaneQuality = planeFit.quality;
      const qualityAdjustedBase = PlaneCreator.CLUSTER_WEIGHT_BASE * cutOffPlaneQuality;

      // Add to cluster points with combined weight based on:
      // 1. Plane fitting quality (how well points fit a plane)
      // 2. Distance from mouse center (exponential falloff)
      if (mouseCenter) {
        for (let j = 0; j < filteredPoints.length; j++) {
          const point = filteredPoints[j];
          const distance = point.distanceTo(mouseCenter);
          const distanceWeight = Math.exp(-PlaneCreator.CLUSTER_WEIGHT_FALLOFF_RATE * distance);
          const weight = qualityAdjustedBase * distanceWeight;
          clusterPoints.push({ point, weight });
        }
      } else {
        // Fallback to quality-adjusted base weight if no center provided
        const weight = qualityAdjustedBase;
        for (let j = 0; j < filteredPoints.length; j++) {
          clusterPoints.push({ point: filteredPoints[j], weight });
        }
      }
      if (enableProfiling) this.profiler.end('weightClusterPoints');

      // Log plane fitting quality for debugging
      if (PlaneCreator.DEBUG_RENDER && planeFit) {
        let maxDistFromCentroid = 0;
        for (const p of filteredPoints) {
          const dist = p.distanceTo(planeFit.centroid);
          maxDistFromCentroid = Math.max(maxDistFromCentroid, dist);
        }
      }
    }
    if (enableProfiling) this.profiler.end('getRandomPointsFromClusters.total');
    return clusterPoints;
  }

  /**
   * Short-lived GPU-pick cache: one fit pass picks the SAME mouse position
   * twice (grid pass + cluster pass), and each pick renders a pick buffer.
   * The window is a couple of frames — a moved camera comes with a new mouse
   * position in practice.
   */
  private lastPick: { x: number; y: number; time: number; result: ReturnType<Viewer['inputHandler']['getMousePointCloudIntersection']> } | null = null;

  private pickPointCloudIntersection(mouse: THREE.Vector2): ReturnType<Viewer['inputHandler']['getMousePointCloudIntersection']> {
    const now = performance.now();
    if (this.lastPick && this.lastPick.x === mouse.x && this.lastPick.y === mouse.y && now - this.lastPick.time < 30) {
      return this.lastPick.result;
    }
    const result = this.viewer.inputHandler.getMousePointCloudIntersection(mouse);
    this.lastPick = { x: mouse.x, y: mouse.y, time: now, result };
    return result;
  }

  private getHoveredClusterSegmentsAtClosestSurface(mouse: THREE.Vector2): SegmentReference[] {
    const camera = this.viewer.scene.getActiveCamera();

    // First, get the intersection point using the standard picker to determine distance
    const rawIntersection = this.pickPointCloudIntersection(mouse);

    if (!rawIntersection || Array.isArray(rawIntersection)) {
      return [];
    }
    const intersection = rawIntersection;

    // Scale ray radius based on distance to camera (farther = bigger, closer = smaller)
    // Max radius is based on the LOD spacing of the picked point, smoothed to reduce outlier jumps
    const distanceToCamera = intersection.distance;
    const pointSpacing = (intersection.point as { spacing?: number })?.spacing ?? 1.0;
    const rawMaxRadius = pointSpacing * RAY_PICK_SPACING_MULTIPLIER;
    const maxRadius = this.smoothMaxRadius(rawMaxRadius);
    const scaledRadius = Math.max(RAY_PICK_MIN_RADIUS, Math.min(maxRadius, distanceToCamera * RAY_PICK_DISTANCE_SCALE_FACTOR));

    // Convert pixel coordinates to NDC space (-1 to 1)
    const size = this.viewer.renderer.getSize(new THREE.Vector2());
    const ndcMouse = new THREE.Vector2((mouse.x / size.width) * 2 - 1, -(mouse.y / size.height) * 2 + 1);

    // Create ray starting 10cm before the picked point (optimization to reduce search distance)
    const cameraOrigin = camera.position.clone();
    const targetPoint = new THREE.Vector3(ndcMouse.x, ndcMouse.y, 0.5).unproject(camera);
    const direction = new THREE.Vector3().subVectors(targetPoint, cameraOrigin).normalize();
    const rayStartOffset = Math.max(0, distanceToCamera - 0.1); // Start 10cm before the picked point
    const origin = cameraOrigin.clone().add(direction.clone().multiplyScalar(rayStartOffset));
    const ray = new THREE.Ray(origin, direction);

    // Get points from first available point cloud
    let pointCloudId = '';
    let points: PointWithSegment[] = [];

    for (const pc of this.viewer.scene.pointclouds) {
      points = getPointsAlongRay(pc as IPointCloud, ray, 1000, scaledRadius, true, this.viewer.scene.volumes, this.viewer.scene.planes);
      if (points.length > 0) {
        pointCloudId = pc.identifier || String(pc.id);
        break;
      }
    }

    if (!points.length) {
      return [];
    }

    // Randomly downsample if we have too many points
    if (points.length > RAY_PICK_MAX_POINTS) {
      points = this.randomSample(points, RAY_PICK_MAX_POINTS);
    }

    // Extract unique segments from the points
    const segmentSet = new Set<number>();
    const segments: SegmentReference[] = [];

    points.forEach((p: PointWithSegment) => {
      const segmentId = p.segment[0];
      if (!segmentSet.has(segmentId)) {
        segmentSet.add(segmentId);
        segments.push({ pointcloudId: pointCloudId, segmentId });
      }
    });

    return segments;
  }

  // Cache for point cloud lookups
  private pointCloudCache: Map<string, PointCloudWithNodes | undefined> = new Map();

  // Cache for sampled segment points (consistent random sample per segment)
  private segmentSamplesCache: Map<string, THREE.Vector3[]> = new Map();

  private extractPointsFromSegment(pointcloudId: string, segmentId: number, maxPoints: number = PlaneCreator.CLUSTER_SAMPLE_SIZE): THREE.Vector3[] {
    const enableProfiling = PlaneCreator.ENABLE_PROFILING;
    if (enableProfiling) this.profiler.start('extractPointsFromSegment.total');

    // Use cached point cloud lookup
    let pointCloud = this.pointCloudCache.get(pointcloudId);
    if (pointCloud === undefined) {
      pointCloud = this.viewer.scene.pointclouds.find((pc) => String(pc.id) === pointcloudId || pc.identifier === pointcloudId) as PointCloudWithNodes | undefined;
      this.pointCloudCache.set(pointcloudId, pointCloud);
    }

    if (!pointCloud) {
      if (enableProfiling) this.profiler.end('extractPointsFromSegment.total');
      return [];
    }

    if (enableProfiling) this.profiler.start('collectPointsFromCache');

    // Collect all points from all visible nodes using the cache
    const allPoints: THREE.Vector3[] = [];
    const visibleNodes = pointCloud.visibleNodes;
    if (visibleNodes) {
      for (let i = 0; i < visibleNodes.length; i++) {
        const node = visibleNodes[i];
        const geometryNode = node.geometryNode || node;
        if (geometryNode && 'getPointsForSegment' in geometryNode && typeof geometryNode.getPointsForSegment === 'function') {
          const matrixWorld = node.sceneNode?.matrixWorld;
          const nodePoints = (geometryNode as { getPointsForSegment: (segmentId: number, matrixWorld?: THREE.Matrix4) => THREE.Vector3[] }).getPointsForSegment(segmentId, matrixWorld);
          // Use loop instead of spread to avoid stack overflow with large arrays
          for (let j = 0; j < nodePoints.length; j++) {
            allPoints.push(nodePoints[j]);
          }
        }
      }
    }

    if (enableProfiling) this.profiler.end('collectPointsFromCache');

    // Apply reservoir sampling if we have more points than needed
    if (enableProfiling) this.profiler.start('reservoirSampling');
    const sampledPoints = allPoints.length <= maxPoints ? allPoints : this.reservoirSample(allPoints, maxPoints);
    if (enableProfiling) this.profiler.end('reservoirSampling');

    if (enableProfiling) this.profiler.end('extractPointsFromSegment.total');
    return sampledPoints;
  }

  private reservoirSample(points: THREE.Vector3[], sampleSize: number): THREE.Vector3[] {
    const result: THREE.Vector3[] = [];

    for (let i = 0; i < points.length; i++) {
      if (i < sampleSize) {
        result.push(points[i]);
      } else {
        const randomIndex = Math.floor(Math.random() * (i + 1));
        if (randomIndex < sampleSize) {
          result[randomIndex] = points[i];
        }
      }
    }

    return result;
  }

  selectedFittingPoints: MousePoints[] = [];

  creationMarkers: PointoramaPlane[] = [];
  creationLines: HelperLine[] = [];
  angleLabel: LabelText;
  debugVisualizer: DebugPointVisualizer; // Debug point visualizer
  permanentDebugSpheres: THREE.Mesh[][] = []; // Permanent spheres for each created plane

  constructor(viewer: Viewer, plane: PointoramaPlane) {
    super();
    this.viewer = viewer;
    this.activePlane = plane;
    this.debugVisualizer = new DebugPointVisualizer(viewer);
    this.profiler = new PerformanceProfiler();

    // Configure RANSAC parameters for better plane fitting
    this.activePlane.maxIterations = PlaneCreator.RANSAC_MAX_ITERATIONS;
    this.activePlane.outlierThreshold = PlaneCreator.RANSAC_OUTLIER_THRESHOLD;
    this.activePlane.minPointsRatio = PlaneCreator.RANSAC_MIN_POINTS_RATIO;

    this.angleLabel = new LabelText(viewer);
    this.angleLabel.visible = false;
    this.angleLabel.offset = true;
    this.add(this.angleLabel);

    viewer.addEventListener('update', this.update.bind(this));
  }

  reset() {
    this.selectedFittingPoints = [];
    this.invalidateAdjustCache();
    this.lastPick = null;
    if (this.adjustRafHandle) {
      cancelAnimationFrame(this.adjustRafHandle);
      this.adjustRafHandle = 0;
      this.pendingAdjustMouse = null;
    }

    for (const marker of this.creationMarkers) {
      this.remove(marker);
    }
    for (const line of this.creationLines) {
      this.remove(line);
    }
    this.creationMarkers = [];
    this.creationLines = [];

    // Clear all debug spheres (temporary and permanent)
    this.debugVisualizer.clear();
    this.clearAllPermanentDebugSpheres();

    // Clear caches
    this.pointCloudCache.clear();
    this.segmentSamplesCache.clear();

    // Reset smoothing state
    this.previousNormal = null;
    this.smoothedMaxRadius = 0.1;

    // Reset cursor
    this.viewer.renderer.domElement.style.cursor = '';

    // Clear profiler when resetting
    if (PlaneCreator.ENABLE_PROFILING) {
      this.profiler.clear();
    }
  }

  // Public methods for profiling control
  getProfilingStats(label: string) {
    return this.profiler.getStats(label);
  }

  printProfilingSummary() {
    this.profiler.printSummary();
  }

  clearProfiling() {
    this.profiler.clear();
  }

  setVisible(visible: boolean) {
    this.activePlane.visible = visible;
    this.angleLabel.setVisible(visible);
    if (visible) {
      this.activePlane.highlight();
    } else {
      this.activePlane.unHighlight();
    }
    this.creationMarkers.forEach((marker) => (marker.visible = visible));
    this.creationLines.forEach((line) => (line.visible = visible));
  }

  /**
   * Check if the mouse is hovering over the active plane or any committed creation markers
   * Used to detect clicks on the plane being created for confirmation
   * Note: The last marker in creationMarkers follows the mouse cursor and is excluded
   */
  isHoveringActivePlaneOrMarkers(): boolean {
    // Get all clickable meshes: activePlane's fullCircle and committed marker fullCircles
    const clickables: THREE.Object3D[] = [];

    if (this.activePlane.visible) {
      clickables.push(this.activePlane.fullCircle);
    }

    // Exclude the last marker since it follows the mouse cursor (uncommitted)
    // Only include markers up to the second-to-last one
    const committedMarkerCount = Math.max(0, this.creationMarkers.length - 1);
    for (let i = 0; i < committedMarkerCount; i++) {
      const marker = this.creationMarkers[i];
      if (marker.visible) {
        clickables.push(marker.fullCircle);
      }
    }

    if (clickables.length === 0) return false;

    // Use the viewer's raycaster to check intersection
    const intersects = this.viewer.inputHandler.raycaster.intersectObjects(clickables);
    return intersects.length > 0;
  }

  /**
   * Update visual state when hovering over the active plane or committed markers
   * - Hides the last (uncommitted) marker that follows the mouse
   * - Sets pointer cursor to indicate clickable action
   * Should be called during mouse move when there are markers placed
   */
  updateHoverState(): void {
    // Only relevant when we have markers placed (plane under construction)
    if (this.selectedFittingPoints.length === 0) {
      return;
    }

    const isHovering = this.isHoveringActivePlaneOrMarkers();
    const lastMarker = this.creationMarkers.at(-1);
    const lastLine = this.creationLines.at(-1);

    if (isHovering) {
      // Hide the last marker and line when hovering over clickable area
      if (lastMarker) lastMarker.visible = false;
      if (lastLine) lastLine.visible = false;
      // Set pointer cursor
      this.viewer.renderer.domElement.style.cursor = 'pointer';
    } else {
      // Show the last marker and line when not hovering
      if (lastMarker) lastMarker.visible = true;
      if (lastLine) lastLine.visible = true;
      // Reset cursor
      this.viewer.renderer.domElement.style.cursor = '';
    }
  }

  /** Pending fit request, coalesced to one execution per animation frame. */
  private pendingAdjustMouse: THREE.Vector2 | null = null;
  private adjustRafHandle = 0;
  /** Mouse position of the last completed fit — unchanged position ⇒ skip. */
  private lastAdjustedMouse: THREE.Vector2 | null = null;

  /**
   * Schedule a plane fit for the next animation frame. Mousemove fires far
   * above the display rate on high-polling mice, and one fit costs two GPU
   * picks plus two octree ray scans — only the last position per frame ever
   * produces a visible result, so intermediate calls are coalesced.
   */
  adjustActivePlaneToMousePosition(mouse: THREE.Vector2): void {
    this.pendingAdjustMouse = mouse.clone();
    if (this.adjustRafHandle) return;
    this.adjustRafHandle = requestAnimationFrame(() => {
      this.adjustRafHandle = 0;
      const pending = this.pendingAdjustMouse;
      this.pendingAdjustMouse = null;
      if (!pending) return;
      // Sub-pixel move with unchanged fitting state: the previous fit stands.
      if (this.lastAdjustedMouse && this.lastAdjustedMouse.distanceToSquared(pending) < 0.25) return;
      this.lastAdjustedMouse = pending.clone();
      this.adjustActivePlaneToMousePositionNow(pending);
    });
  }

  /** Marker/selection changes invalidate the unchanged-position skip. */
  private invalidateAdjustCache(): void {
    this.lastAdjustedMouse = null;
  }

  private adjustActivePlaneToMousePositionNow(mouse: THREE.Vector2) {
    const enableProfiling = PlaneCreator.ENABLE_PROFILING;
    if (enableProfiling) this.profiler.start('adjustActivePlaneToMousePosition');

    if (enableProfiling) this.profiler.start('getPointsAroundMouse');
    const mousePoints = this.getPointsAroundMouse(mouse);
    if (enableProfiling) this.profiler.end('getPointsAroundMouse');

    if (!mousePoints) {
      this.setVisible(!!this.creationMarkers.length); // while creating markers we want to see the active plane & label
      if (enableProfiling) this.profiler.end('adjustActivePlaneToMousePosition');
      return;
    }
    this.setVisible(true);

    if (enableProfiling) this.profiler.start('buildGridWeightedPoints');
    // Create separate weighted point arrays for grid and cluster points
    const gridWeightedPoints: WeightedPoint[] = [];

    // Add current mouse grid points with exponential falloff weight
    const gridLen = mousePoints.grid.length;
    for (let i = 0; i < gridLen; i++) {
      const p = mousePoints.grid[i];
      const weight = PlaneCreator.calculateGridWeight(p, mousePoints.center);
      gridWeightedPoints.push({ point: p, weight });
    }

    // Add current mouse center point with high weight
    gridWeightedPoints.push({ point: mousePoints.center, weight: PlaneCreator.CENTER_WEIGHT });

    // Add grid points with exponential falloff weight
    const selectedLen = this.selectedFittingPoints.length;
    for (let i = 0; i < selectedLen; i++) {
      const fp = this.selectedFittingPoints[i];
      const fpGridLen = fp.grid.length;
      for (let j = 0; j < fpGridLen; j++) {
        const p = fp.grid[j];
        const weight = PlaneCreator.calculateGridWeight(p, fp.center);
        gridWeightedPoints.push({ point: p, weight });
      }
      // Add center points with high weight
      gridWeightedPoints.push({ point: fp.center, weight: PlaneCreator.CENTER_WEIGHT });
    }
    if (enableProfiling) this.profiler.end('buildGridWeightedPoints');

    // Fit plane to grid points first for cluster point filtering
    if (enableProfiling) this.profiler.start('fitPlaneToPoints');
    const gridPlane = this.fitPlaneToPoints(gridWeightedPoints) || undefined;
    if (enableProfiling) this.profiler.end('fitPlaneToPoints');

    if (enableProfiling) this.profiler.start('collectStoredClusterPoints');
    // Collect stored cluster points from confirmed markers
    const storedClusterPoints: WeightedPoint[] = [];
    for (let i = 0; i < selectedLen; i++) {
      const fp = this.selectedFittingPoints[i];
      if (fp.clusterPoints) {
        storedClusterPoints.push(...fp.clusterPoints);
      }
    }
    if (enableProfiling) this.profiler.end('collectStoredClusterPoints');

    // Get cluster points from current mouse position and filter them based on grid plane
    if (enableProfiling) this.profiler.start('getRandomPointsFromClusters');
    const freshClusterPoints = this.getRandomPointsFromClusters(mouse, mousePoints.center, gridPlane);
    if (enableProfiling) this.profiler.end('getRandomPointsFromClusters');

    // Combine all points and set rotation with smoothing
    if (enableProfiling) this.profiler.start('setRotationFromPoints');
    const allWeightedPoints = [...gridWeightedPoints, ...storedClusterPoints, ...freshClusterPoints];
    const cameraPosition = this.viewer.scene.getActiveCamera().position;

    // Get the new normal from plane fitting
    const planeFit = this.fitPlaneToPoints(allWeightedPoints);
    if (planeFit) {
      // Ensure normal points towards camera
      const planeToCamera = new THREE.Vector3().subVectors(cameraPosition, planeFit.centroid).normalize();
      if (planeFit.normal.dot(planeToCamera) < 0) {
        planeFit.normal.negate();
      }

      // Apply smoothing to the normal
      const smoothedNormal = this.smoothNormal(planeFit.normal);

      // Set the rotation using the smoothed normal
      this.activePlane.setRotationFromNormal(smoothedNormal);
      // Calculate inclination and bearing for angle label display
      this.activePlane.calculateInclinationAndBearing();
    }
    if (enableProfiling) this.profiler.end('setRotationFromPoints');

    // Create debug spheres to visualize the selected points
    if (PlaneCreator.DEBUG_RENDER) {
      this.createDebugSpheres(mousePoints, freshClusterPoints, gridPlane);
    }

    if (this.selectedFittingPoints.length === 0) {
      this.moveActivePlane(mousePoints);
    } else {
      this.moveLastMarker(mousePoints);
    }

    // Update hover state to show/hide last marker and set cursor
    this.updateHoverState();

    if (enableProfiling) {
      this.profiler.end('adjustActivePlaneToMousePosition');
      this.profiler.printSummary();
    }
  }

  adjustActivePlane() {
    // Check if all markers are snapped - if so, use the snapped normal directly
    const snappedMarkers = this.selectedFittingPoints.filter((fp) => fp.isSnapped);
    const allMarkersSnapped = snappedMarkers.length > 0 && snappedMarkers.length === this.selectedFittingPoints.length;

    if (allMarkersSnapped && snappedMarkers.length > 0) {
      // All markers are snapped, combine their snapped normals
      const combinedNormal = new THREE.Vector3();
      let validNormalCount = 0;

      snappedMarkers.forEach((marker) => {
        if (marker.snappedNormal) {
          combinedNormal.add(marker.snappedNormal);
          validNormalCount++;
        }
      });

      if (validNormalCount > 0) {
        combinedNormal.divideScalar(validNormalCount);
        combinedNormal.normalize();
        this.activePlane.setRotationFromNormal(combinedNormal);
        this.activePlane.calculateInclinationAndBearing();
        return;
      }
    }

    // Create weighted grid points for final plane
    const gridWeightedPoints: WeightedPoint[] = [];

    // Add grid points with exponential falloff weight
    this.selectedFittingPoints.forEach((fp) => {
      fp.grid.forEach((p) => {
        const weight = PlaneCreator.calculateGridWeight(p, fp.center);
        gridWeightedPoints.push({ point: p, weight });
      });
    });

    // Add center points with high weight
    this.selectedFittingPoints.forEach((fp) => {
      gridWeightedPoints.push({ point: fp.center, weight: PlaneCreator.CENTER_WEIGHT });
    });

    // Collect all stored cluster points from all markers
    const allClusterPoints: WeightedPoint[] = [];
    this.selectedFittingPoints.forEach((fp) => {
      if (fp.clusterPoints) {
        allClusterPoints.push(...fp.clusterPoints);
      }
    });

    // Combine all points and set rotation
    const allWeightedPoints = [...gridWeightedPoints, ...allClusterPoints];
    const cameraPosition = this.viewer.scene.getActiveCamera().position;
    this.activePlane.setRotationFromPoints(allWeightedPoints, cameraPosition);
  }

  getPointsAroundMouse(mouse: THREE.Vector2) {
    const enableProfiling = PlaneCreator.ENABLE_PROFILING;
    if (enableProfiling) this.profiler.start('getPointsAroundMouse.total');

    if (enableProfiling) this.profiler.start('raycastIntersection');
    const camera = this.viewer.scene.getActiveCamera();

    // First, get the intersection point using the standard picker to determine distance
    const rawIntersection = this.pickPointCloudIntersection(mouse);

    if (!rawIntersection || Array.isArray(rawIntersection)) {
      if (enableProfiling) this.profiler.end('raycastIntersection');
      if (enableProfiling) this.profiler.end('getPointsAroundMouse.total');
      return null;
    }
    const intersection = rawIntersection;

    // Scale ray radius based on distance to camera (farther = bigger, closer = smaller)
    // Max radius is based on the LOD spacing of the picked point, smoothed to reduce outlier jumps
    const distanceToCamera = intersection.distance;
    const pointSpacing = (intersection.point as { spacing?: number })?.spacing ?? 1.0;
    const rawMaxRadius = pointSpacing * RAY_PICK_SPACING_MULTIPLIER;
    const maxRadius = this.smoothMaxRadius(rawMaxRadius);
    const scaledRadius = Math.max(RAY_PICK_MIN_RADIUS, Math.min(maxRadius, distanceToCamera * RAY_PICK_DISTANCE_SCALE_FACTOR));

    // Convert pixel coordinates to NDC space (-1 to 1)
    const size = this.viewer.renderer.getSize(new THREE.Vector2());
    const ndcMouse = new THREE.Vector2((mouse.x / size.width) * 2 - 1, -(mouse.y / size.height) * 2 + 1);

    // Create ray starting 10cm before the picked point (optimization to reduce search distance)
    const cameraOrigin = camera.position.clone();
    const targetPoint = new THREE.Vector3(ndcMouse.x, ndcMouse.y, 0.5).unproject(camera);
    const direction = new THREE.Vector3().subVectors(targetPoint, cameraOrigin).normalize();
    const rayStartOffset = Math.max(0, distanceToCamera - 0.1); // Start 10cm before the picked point
    const origin = cameraOrigin.clone().add(direction.clone().multiplyScalar(rayStartOffset));
    const ray = new THREE.Ray(origin, direction);

    // Collect points from all point clouds
    let allPointsWithSegments: Array<PointWithSegment & { pointCloudId: string }> = [];
    for (const pc of this.viewer.scene.pointclouds) {
      const points = getPointsAlongRay(pc as IPointCloud, ray, 1000, scaledRadius, true, this.viewer.scene.volumes, this.viewer.scene.planes);
      // Add pointCloudId to each point and append to array
      // Using loop instead of spread to avoid stack overflow with large arrays
      for (let i = 0; i < points.length; i++) {
        allPointsWithSegments.push({
          ...points[i],
          pointCloudId: pc.identifier || String(pc.id),
        });
      }
    }

    // Randomly downsample if we have too many points
    if (allPointsWithSegments.length > RAY_PICK_MAX_POINTS) {
      allPointsWithSegments = this.randomSample(allPointsWithSegments, RAY_PICK_MAX_POINTS);
    }

    if (enableProfiling) this.profiler.end('raycastIntersection');

    if (!allPointsWithSegments.length) {
      if (enableProfiling) this.profiler.end('getPointsAroundMouse.total');
      return null;
    }

    // Convert to PointCloudIntersection format and deduplicate
    const deduplicationTolerance = 0.001; // 1mm for deduplication
    const allFilteredPoints: PointCloudIntersection[] = [];
    const seen = new Set<number>(); // Use numeric hash for faster lookup

    for (let i = 0; i < allPointsWithSegments.length; i++) {
      const p = allPointsWithSegments[i];
      // Deduplication using numeric hash (much faster than string concatenation)
      const x = Math.round(p.position.x / deduplicationTolerance);
      const y = Math.round(p.position.y / deduplicationTolerance);
      const z = Math.round(p.position.z / deduplicationTolerance);
      // Cantor pairing function for unique hash
      const hash = ((x + y) * (x + y + 1)) / 2 + y + z * 1000000;

      if (!seen.has(hash)) {
        seen.add(hash);
        allFilteredPoints.push({
          position: p.position,
          pointCloudId: p.pointCloudId,
          segment: p.segment,
          distanceToRay: p.distanceToRay,
        });
      }
    }

    // Randomly sample points instead of picking closest to ray
    const selectedPoints = this.randomSample(allFilteredPoints, PlaneCreator.MAX_GRID_POINTS);

    const grid = selectedPoints.map((p: PointCloudIntersection) => p.position);

    // Calculate center as the average of all selected points
    if (enableProfiling) this.profiler.start('calculateCenter');
    const center = new THREE.Vector3(0, 0, 0);
    for (let i = 0; i < grid.length; i++) {
      center.add(grid[i]);
    }
    if (grid.length > 0) {
      center.divideScalar(grid.length);
    }
    if (enableProfiling) this.profiler.end('calculateCenter');

    if (enableProfiling) this.profiler.end('getPointsAroundMouse.total');
    return { center, grid };
  }

  moveActivePlane(mousePoints: MousePoints) {
    this.activePlane.position.set(mousePoints.center.x, mousePoints.center.y, mousePoints.center.z);
    this.position.set(mousePoints.center.x, mousePoints.center.y, mousePoints.center.z);
    this.updateAngleLabel();
  }

  moveLastMarker(mousePoints: MousePoints) {
    const lastMarker = this.creationMarkers.at(-1);
    if (!lastMarker) return;
    const cameraPosition = this.viewer.scene.getActiveCamera().position;
    lastMarker.setRotationFromPoints(mousePoints.grid, cameraPosition);
    const relativePosition = mousePoints.center.clone().sub(this.position);
    lastMarker.position.set(relativePosition.x, relativePosition.y, relativePosition.z);
    this.creationLines.at(-1)?.setBetweenPoints([new THREE.Vector3(0, 0, 0), relativePosition]);
    this.updateAngleLabel();
  }

  /**
   * Add a marker at the mouse position
   * @returns 'added' if marker was added, 'finish' if caller should finish the plane (clicked on active plane/markers), 'none' if nothing happened
   */
  addMarker(mouse: THREE.Vector2): 'added' | 'finish' | 'none' {
    this.invalidateAdjustCache();
    // If we already have markers and user clicks on the active plane or markers, signal to finish
    if (this.selectedFittingPoints.length > 0 && this.isHoveringActivePlaneOrMarkers()) {
      return 'finish';
    }

    const rawMousePoints = this.getPointsAroundMouse(mouse);
    if (!rawMousePoints) return 'none';

    // Get cluster points for this marker and store them
    // First create grid plane from existing markers + current mouse points
    const tempGridWeightedPoints: WeightedPoint[] = [];

    // Add current mouse grid points
    rawMousePoints.grid.forEach((p) => {
      const weight = PlaneCreator.calculateGridWeight(p, rawMousePoints.center);
      tempGridWeightedPoints.push({ point: p, weight });
    });
    tempGridWeightedPoints.push({ point: rawMousePoints.center, weight: PlaneCreator.CENTER_WEIGHT });

    // Add existing selected grid points
    this.selectedFittingPoints.forEach((fp) => {
      fp.grid.forEach((p) => {
        const weight = PlaneCreator.calculateGridWeight(p, fp.center);
        tempGridWeightedPoints.push({ point: p, weight });
      });
      tempGridWeightedPoints.push({ point: fp.center, weight: PlaneCreator.CENTER_WEIGHT });
    });

    // Fit temp grid plane
    const tempGridPlane = this.fitPlaneToPoints(tempGridWeightedPoints) || undefined;

    // Get cluster points for this specific marker
    const clusterPoints = this.getRandomPointsFromClusters(mouse, rawMousePoints.center, tempGridPlane);

    // Create the complete MousePoints object with cluster data and snap info
    const mousePoints: MousePoints = {
      center: rawMousePoints.center,
      grid: rawMousePoints.grid,
      clusterPoints: clusterPoints,
      isSnapped: this.activePlane.snapped,
      snappedNormal: this.activePlane.snapped ? this.activePlane.getNormal() : undefined,
    };

    this.selectedFittingPoints.push(mousePoints);

    // Reset smoothing when adding a marker (new context for plane fitting)
    this.previousNormal = null;

    // Make the current debug spheres permanent for this plane marker
    if (PlaneCreator.DEBUG_RENDER) {
      this.makePermanentDebugSpheres();
    }

    this.addMarkerToScene(mousePoints.center, mousePoints.grid);

    // Set pointer cursor since user can now click on plane/markers to finish
    this.viewer.renderer.domElement.style.cursor = 'pointer';

    return 'added';
  }

  addMarkerToScene(position: THREE.Vector3, gridPoints?: THREE.Vector3[]) {
    const newMarker = new PointoramaPlane();
    const relativePosition = position.clone().sub(this.position);
    newMarker.position.set(relativePosition.x, relativePosition.y, relativePosition.z);
    if (gridPoints) {
      const cameraPosition = this.viewer.scene.getActiveCamera().position;
      newMarker.setRotationFromPoints(gridPoints, cameraPosition);
    }
    newMarker.color = new THREE.Color(DefaultColors.TEMPORARY_OBJECTS);
    this.add(newMarker);
    this.creationMarkers.push(newMarker);

    const newLine = new HelperLine();
    newLine.setBetweenPoints([new THREE.Vector3(0, 0, 0), relativePosition]);
    this.add(newLine);
    this.creationLines.push(newLine);
  }

  removeMarker() {
    this.invalidateAdjustCache();
    this.selectedFittingPoints.pop();
    const lastMarker = this.creationMarkers.pop();
    const lastLine = this.creationLines.pop();

    // Reset smoothing when removing a marker (context changes)
    this.previousNormal = null;

    // Remove the associated permanent debug spheres
    if (PlaneCreator.DEBUG_RENDER) {
      this.removeLastPermanentDebugSpheres();
    }

    if (lastMarker && lastLine) {
      this.remove(lastMarker);
      this.remove(lastLine);
    }
  }

  update() {
    this.updateAngleLabel();
    this.updateMarkerSize();
  }

  updateAngleLabel() {
    if (!this.angleLabel) return;

    this.angleLabel.visible = this.activePlane.visible;
    const inclinationDeg = this.activePlane.getInclinationDegrees().toFixed(2);
    this.angleLabel.setText(inclinationDeg + '°');
  }

  updateMarkerSize() {
    if (!this.viewer.scene) {
      return;
    }

    const marker = this.selectedFittingPoints.length > 0 ? this.creationMarkers.at(-1) : this.activePlane;
    if (!marker) return;

    const scale = this.viewer.calculatePlaneRadius(marker);
    marker.setRadius(scale);
    marker.radius = scale;
  }

  clearDebugSpheres() {
    this.debugVisualizer.clear();
  }

  clearAllPermanentDebugSpheres() {
    for (const sphereGroup of this.permanentDebugSpheres) {
      for (const sphere of sphereGroup) {
        this.viewer.scene.scene.remove(sphere);
      }
    }
    this.permanentDebugSpheres = [];
  }

  makePermanentDebugSpheres() {
    // Move current debug spheres to permanent storage
    this.permanentDebugSpheres.push(this.debugVisualizer.releaseCurrentSpheres());
  }

  removeLastPermanentDebugSpheres() {
    const lastSphereGroup = this.permanentDebugSpheres.pop();
    if (lastSphereGroup) {
      for (const sphere of lastSphereGroup) {
        this.viewer.scene.scene.remove(sphere);
      }
    }
  }

  createDebugSpheres(mousePoints: MousePoints, clusterPoints: WeightedPoint[] = [], gridPlane?: PlaneData) {
    this.debugVisualizer.clear();
    // Scale debug visualization based on point cloud spacing
    const spacing = this.viewer.scene.pointclouds[0]?.pcoGeometry?.spacing ?? 0.02;
    const pointsize = spacing * 0.02;

    // Combine all points and weights for visualization
    const gridPoints: THREE.Vector3[] = [];
    const gridWeights: number[] = [];

    // Add grid points
    mousePoints.grid.forEach((position) => {
      const weight = PlaneCreator.calculateGridWeight(position, mousePoints.center);
      gridPoints.push(position);
      gridWeights.push(weight);
    });

    this.debugVisualizer.visualizePoints(gridPoints, gridWeights, PlaneCreator.MAX_WEIGHT, pointsize * 1.5);

    const otherPoints: THREE.Vector3[] = [];
    const otherWeights: number[] = [];

    // Add center point
    otherPoints.push(mousePoints.center);
    otherWeights.push(PlaneCreator.CENTER_WEIGHT);

    // Add cluster points
    clusterPoints.forEach((cp) => {
      otherPoints.push(cp.point);
      otherWeights.push(cp.weight);
    });

    // Use the visualizer to render all points
    this.debugVisualizer.visualizePoints(otherPoints, otherWeights, PlaneCreator.MAX_WEIGHT, pointsize);

    // Visualize normals if available (scale line lengths with spacing)
    const normalLineLength = spacing * 5;
    if (gridPlane) {
      // Magenta line for grid plane normal
      this.debugVisualizer.visualizePlaneNormal(
        gridPlane.centroid,
        gridPlane.normal,
        normalLineLength,
        new THREE.Color(0xff00ff), // Magenta color
      );
    }

    // Green line for final active plane normal
    this.debugVisualizer.visualizePlaneNormal(
      this.activePlane.position,
      this.activePlane.getNormal(),
      normalLineLength * 2,
      new THREE.Color(0x00ff00), // Green color for final plane normal
    );
  }
}
